/**
 * PM-SHRI API Service
 * 
 * Migrated to use centralized baseApi with:
 * - Auth token in every request (via baseQueryWithReauth)
 * - 401 Unauthorized → auto logout + redirect
 * - Shared cache and middleware with all other modules
 */

import { baseApi } from '../../../../store/api/baseApi';
import type { ActivityTreeResponse, ExpenseActivityTreeResponse, ExpenditureActivityTreeResponse } from '../types';

type PillarMoney = { rec: number; nonRec: number; total: number };

export const pmShriApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================
    // Activity Tree Endpoints
    // ============================================

    /**
     * Get PM SHRI Activity Tree structure
     * Returns hierarchical tree of all activities organized by Head > SubHead > Activity > SubActivity
     */
    getActivityTree: builder.query<ActivityTreeResponse, void>({
      query: () => ({
        url: '/pmshri/pmshriactivitytree',
        method: 'GET',
      }),
      providesTags: ['ActivityTree' as const],
      keepUnusedDataFor: 600, // Activity tree rarely changes - cache for 10 mins
    }),

    /**
     * Get PM SHRI Expense Activity Tree structure with financial data
     * Returns hierarchical tree with sanctioned and released amounts
     */
    getExpenseActivityTree: builder.query<ExpenseActivityTreeResponse, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/pmshriexpenseactivitytree${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Expense' as const],
    }),

    /**
     * Get PM SHRI Expenditure Activity Tree structure with sanTotal and spentTotal
     * Returns hierarchical tree with sanctioned and spent amounts (recurring and non-recurring)
     */
    getExpenditureActivityTree: builder.query<ExpenditureActivityTreeResponse, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/pmshrixpendatureactivitytree${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Expense' as const],
    }),

    /**
     * Get monthly fund flow and utilization trend for a specific year and optional phase
     */
    getMonthlyFundFlowTrend: builder.query<import('../types').MonthlyFundFlowTrendResponse, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/monthlyfundflowutilizationtrend${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get yearly fund flow and utilization trend (all years aggregated)
     */
    getYearlyFundFlowTrend: builder.query<import('../types').MonthlyFundFlowTrendResponse, { phase?: number }>({
      query: ({ phase }) => ({
        url: `/pmshri/yearlyfundflowutilizationtrend${phase ? `?phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    // ============================================
    // Financial Data Endpoints
    // ============================================

    /**
     * Get total sanctioned amount for a specific year and optional phase
     */
    getTotalSanctionedAmount: builder.query<{ success: boolean; data: any }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/totalsanctionedamount${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get total received amount for a specific year and optional phase
     */
    getTotalReceivedAmount: builder.query<{ success: boolean; data: any }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/totalrecievedamount${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get total spent amount for a specific year and optional phase
     */
    getTotalSpentAmount: builder.query<{ success: boolean; data: any }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/totalspentamount${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get recurring and non-recurring amounts for a specific year and optional phase
     */
    getRecNonRecAmount: builder.query<{ success: boolean; data: { totalAmountRecieved: number; recurringAmount: number; nonRecurringAmount: number } }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/recnonrecamount${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get total number of PM SHRI schools
     */
    getTotalPMSHRISchools: builder.query<{ success: boolean; data: { totalSchools: number } }, void>({
      query: () => ({
        url: '/pmshri/totalschools',
        method: 'GET',
      }),
      providesTags: ['ActivityTree' as const],
      keepUnusedDataFor: 3600, // School count rarely changes - cache for 1 hour
    }),

    /**
     * Get utilization and efficiency percentage for a specific year and optional phase
     */
    getUtilizationEfficiency: builder.query<{
      success: boolean;
      data: {
        Utilization: {
          Spent: number;
          Received: number;
          'Spent Efficiency': string;
          'total Units': number;
          'Units Covered': number;
          'Units unCovered': number;
        }
      }
    }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/utilizationefficiencypercentage${year ? `?year=${year}` : ''}${phase ? `${year ? '&' : '?'}phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get top performing schools
     */
    getTopPerformingSchools: builder.query<{
      success: boolean;
      data: {
        data: Array<{
          kvID: number;
          schoolName: string;
          isPMShree: number;
          phase: number;
          roName: string;
          stateName: string;
          city: string;
          districtName: string;
          sanctionedAmount: number;
          receivedAmount: number;
          spentAmount: number;
          /** Sanctioned − Spent: allotted but not yet spent. */
          sanctionedGap?: number;
          /** Released − Spent. Negative flags releases not yet keyed in. */
          unspentAmount?: number;
          utilizationVal?: number;
          utilization: string;
        }>;
        pagination: {
          total: number;
          currentPage: number;
          limit: number;
          totalPages: number;
        };
      }
    }, { page?: number; year?: number; phase?: number; limit?: number }>({
      query: ({ page = 1, year, phase, limit = 50 }) => ({
        url: `/pmshri/topperformingschools?page=${page}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}&orderby=utilization&order=desc&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get bottom performing schools
     */
    getBottomPerformingSchools: builder.query<{
      success: boolean;
      data: {
        data: Array<{
          kvID: number;
          schoolName: string;
          isPMShree: number;
          phase: number;
          roName: string;
          stateName: string;
          city: string;
          districtName: string;
          sanctionedAmount: number;
          receivedAmount: number;
          spentAmount: number;
          /** Sanctioned − Spent: allotted but not yet spent. */
          sanctionedGap?: number;
          /** Released − Spent. Negative flags releases not yet keyed in. */
          unspentAmount?: number;
          utilizationVal?: number;
          utilization: string;
        }>;
        pagination: {
          total: number;
          currentPage: number;
          limit: number;
          totalPages: number;
        };
      }
    }, { page?: number; year?: number; phase?: number; limit?: number }>({
      query: ({ page = 1, year, phase, limit = 50 }) => ({
        url: `/pmshri/topperformingschools?page=${page}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}&orderby=utilization&order=asc&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get top region-wise performance
     */
    getTopRegionWisePerformance: builder.query<{
      success: boolean;
      data: {
        data: Array<{
          id: number;
          roid: number;
          roName: string;
          name: string;
          stateid: number;
          stateName: string;
          isPMShree: number;
          phase: number;
          totalSanctionedAmount: number;
          totalReceivedAmount: number;
          recurringAmount: number;
          nonRecurringAmount: number;
          totalSpentAmount: number;
          utilization: string;
        }>;
      }
    }, { year?: number; phase?: number }>({
      query: ({ year, phase }) => ({
        url: `/pmshri/topregionwiseperformance?phase=${phase !== undefined ? phase : 1}&orderby=phase&order=desc${year ? `&year=${year}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get top performing schools region-wise
     */
    getTopPerformingRoWise: builder.query<{
      success: boolean;
      data: {
        data: Array<{
          roName: string;
          roID: number;
          schools: Array<{
            kvID: number;
            schoolName: string;
            sanctionedAmount: number;
            receivedAmount: number;
            recurringAmount: number;
            nonRecurringAmount: number;
            spentAmount: number;
            utilization: number | string;
          }>;
        }>;
      }
    }, { page?: number; year?: number; phase?: number; roid: number; limit?: number }>({
      query: ({ page = 1, year, phase, roid, limit = 500 }) => ({
        url: `/pmshri/topperformingrowise?page=${page}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}&roid=${roid}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get school details
     */
    getSchoolDetails: builder.query<{
      success: boolean;
      data: {
        profile: {
          schoolID: number;
          schoolName: string;
          roID: number;
          roName: string;
          stateID: number;
          stateName: string;
          districtName: string;
          city: string;
          isPMShree: number;
          phase: number;
        };
        financials: {
          year: number | string;
          sanctionedAmount: number;
          receivedAmount: number;
          recurringAmount: number;
          nonRecurringAmount: number;
          spentAmount: number;
          spentRec: number;
          spentNonRec: number;
          /** Sanctioned split by activity recurringType (NULL counts as non-recurring). */
          sanctionedRec?: number;
          sanctionedNonRec?: number;
          /** Interest earned on the PM SHRI account — reported beside Released, never folded into it. */
          bankInterest?: number;
          /** Bank charges levied on the account. */
          bankCharges?: number;
          /** Released − Spent. Negative where releases have not been keyed in. */
          unspentAmount?: number;
          /** Spend on the DATE basis — what Expense Details for this year totals. */
          spentInYear?: number;
          /** spentInYear − spentAmount. Non-zero only when a year is selected. */
          basisGap?: number;
          /** Sanctioned − Spent: allocation not yet used. */
          sanctionedGap?: number;
          sanctionedGapRec?: number;
          sanctionedGapNonRec?: number;
          utilization: string;
        };
        breakdown: Array<{
          head: string;
          sanctioned: number;
          spent: number;
          utilization: string;
        }>;
      }
    }, { kvid: number; year?: number }>({
      query: ({ kvid, year }) => ({
        url: `/pmshri/pmshridetail?kvid=${kvid}${year ? `&year=${year}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get school heads breakdown detail
     */
    getSchoolHeadsBreakdown: builder.query<{
      success: boolean;
      data: {
        profile: {
          schoolID: number;
          schoolName: string;
          roID: number;
          roName: string;
          stateID: number;
          stateName: string;
          districtName: string;
          city: string;
          isPMShree: number;
          phase: number;
        };
        financials: {
          year: number;
          sanctionedAmount: number;
          receivedAmount: number;
          recurringAmount: number;
          nonRecurringAmount: number;
          spentAmount: number;
          utilization: string;
        };
        breakdown: Array<{
          head: string;
          sanctioned: number;
          spent: number;
          utilization: string;
        }>;
      }
    }, { kvid: number; year?: number; phase?: number }>({
      query: ({ kvid, year, phase }) => ({
        url: `/pmshri/pmshriheadsbreakdowndetail?kvid=${kvid}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get school activity-wise detail
     */
    getSchoolActivityWiseDetail: builder.query<{
      success: boolean;
      data: {
        profile: {
          schoolID: number;
          schoolName: string;
          roID: number;
          roName: string;
          stateID: number;
          stateName: string;
          districtName: string;
          city: string;
          isPMShree: number;
          phase: number;
        };
        activityWise: Array<{
          activityName: string;
          sanctioned: number;
          spent: number;
          utilization: string;
        }>;
        totals: {
          year: number;
          totalSanctioned: number;
          totalSpent: number;
        };
      }
    }, { kvid: number; year?: number }>({
      query: ({ kvid, year }) => ({
        url: `/pmshri/pmshriactivitywisedetail?kvid=${kvid}${year ? `&year=${year}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get school expense detail
     */
    getSchoolExpenseDetail: builder.query<{
      success: boolean;
      data: {
        profile: {
          schoolID: number;
          schoolName: string;
          roID: number;
          roName: string;
          stateID: number;
          stateName: string;
          districtName: string;
          city: string;
          isPMShree: number;
          phase: number;
        };
        expenses: Array<{
          id: number;
          activityName: string;
          expenditureAmount: number;
          expenditureQuantity: number;
          expenditureDate: string;
        }>;
      }
    }, { kvid: number; year?: number }>({  
      query: ({ kvid, year }) => ({
        url: `/pmshri/pmshriexpensedetail?kvid=${kvid}${year ? `&year=${year}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get monthly fund flow and utilization trend for a specific school (KV)
     */
    getSchoolMonthlyFundTrend: builder.query<{
      success: boolean;
      data: Record<string, {
        totalSanctioned: number;
        totalReceived: number;
        totalSpent: number;
        totalUtilization: string;
      }>;
    }, { kvid: number; year?: number }>({
      query: ({ kvid, year }) => ({
        url: `/pmshri/kvsmonthlyfundflowutilitrend?kvid=${kvid}${year ? `&year=${year}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Pillar-wise expenditure for one KV.
     * Pillar -> Sub Head -> Activity -> Sub Activity, sanctioned vs spent,
     * each split recurring / non-recurring.
     */
    /** Sanctioned Heads & Budget grid — one row per sanctioned head. */
    getSchoolSanctionedHeadsBudget: builder.query<{
      success: boolean;
      data: {
        year: number | string;
        phase: number | string;
        kvID: number;
        data: Array<{
          sn: number;
          sanctionedID: number;
          head: string;
          subHead: string;
          activity: string;
          subActivity: string;
          activityType: string;
          physicalUnits: number;
          unitCost: number;
          amount: number;
          statusCode: number | null;
          status: string;
          expenditureAmount: number;
          expenditureUnit: number;
          year: number | null;
        }>;
        totals: { rows: number; physicalUnits: number; amount: number; expenditureAmount: number; expenditureUnit: number };
      };
    }, { kvid: number; year?: number; phase?: number }>({
      query: ({ kvid, year, phase }) => ({
        url: `/pmshri/sanctionedheadsbudget?kvid=${kvid}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    getSchoolPillarWiseExpenditure: builder.query<{
      success: boolean;
      data: {
        year: number | string;
        phase: number | string;
        kvID: number;
        pillars: Array<{
          id: number;
          title: string;
          sanctioned: PillarMoney;
          spent: PillarMoney;
          subHeads: Array<{
            id: number;
            title: string;
            sanctioned: PillarMoney;
            spent: PillarMoney;
            activities: Array<{
              id: number;
              title: string;
              sanctioned: PillarMoney;
              spent: PillarMoney;
              subActivities: Array<{
                id: number;
                title: string;
                sanctioned: PillarMoney;
                spent: PillarMoney;
              }>;
            }>;
          }>;
        }>;
        summary: Array<{ id: number; title: string; sanctioned: PillarMoney; spent: PillarMoney }>;
        grandTotal: { sanctioned: PillarMoney; spent: PillarMoney };
      };
    }, { kvid: number; year?: number; phase?: number }>({
      query: ({ kvid, year, phase }) => ({
        url: `/pmshri/pillarwiseexpenditure?kvid=${kvid}${year ? `&year=${year}` : ''}${phase ? `&phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),

    /**
     * Get region heads breakdown detail
     */
    getRegionHeadsBreakdown: builder.query<{
      success: boolean;
      data: {
        roID: number;
        schoolCount: number;
        financials: {
          year: string;
          sanctionedAmount: number;
          receivedAmount: number;
          spentAmount: number;
          utilization: string;
        };
        breakdown: Array<{
          head: string;
          sanctioned: number;
          spent: number;
          spentRec: number;
          spentNonRec: number;
          utilization: string;
        }>;
      }
    }, { roid: number; year?: number | null; phase?: number | null }>({
      query: ({ roid, year, phase }) => ({
        url: `/pmshri/region-pmshriheadsbreakdowndetail?roid=${roid}${year !== undefined && year !== null ? `&year=${year}` : ''}${phase !== undefined && phase !== null ? `&phase=${phase}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Financial' as const],
    }),
  }),
  overrideExisting: false,
});

// Export hooks for usage in functional components
export const {
  useGetActivityTreeQuery,
  useGetExpenseActivityTreeQuery,
  useGetExpenditureActivityTreeQuery,
  useGetTotalSanctionedAmountQuery,
  useGetTotalReceivedAmountQuery,
  useGetTotalSpentAmountQuery,
  useGetRecNonRecAmountQuery,
  useGetTotalPMSHRISchoolsQuery,
  useGetUtilizationEfficiencyQuery,
  useGetMonthlyFundFlowTrendQuery,
  useGetYearlyFundFlowTrendQuery,
  useGetTopPerformingSchoolsQuery,
  useGetBottomPerformingSchoolsQuery,
  useGetTopRegionWisePerformanceQuery,
  useGetTopPerformingRoWiseQuery,
  useGetSchoolDetailsQuery,
  useGetSchoolHeadsBreakdownQuery,
  useGetSchoolActivityWiseDetailQuery,
  useGetSchoolExpenseDetailQuery,
  useGetSchoolPillarWiseExpenditureQuery,
  useGetSchoolSanctionedHeadsBudgetQuery,
  useGetSchoolMonthlyFundTrendQuery,
  useGetRegionHeadsBreakdownQuery,
} = pmShriApi;
